import { QRCodeSVG } from "qrcode.react";
import Modal from "@modalSrc/Modal";
import { toast } from "react-toastify";
import { copyUrlAsLink } from "@/utils/copyUrlAsLink";

type QrModalProps = {
    isOpen: boolean;
    onClose: () => void;
    content: string;
};

export default function QrModal({ isOpen, onClose, content }: QrModalProps) {
    if (!isOpen) return null;

    const handleDownloadAndCopy = async () => {
        try {
            // Get the SVG element
            const svgElement = document.querySelector(".qr-code svg") as SVGSVGElement | null;
            if (!svgElement) {
                console.error("SVG element not found");
                return;
            }

            // Create a canvas element
            const canvas = document.createElement("canvas");
            const context = canvas.getContext("2d");

            // Set canvas dimensions to match the SVG
            const svgWidth = svgElement.getBoundingClientRect().width;
            const svgHeight = svgElement.getBoundingClientRect().height;
            canvas.width = svgWidth;
            canvas.height = svgHeight;

            // Convert the SVG to a data URL
            const svgData = new XMLSerializer().serializeToString(svgElement);
            const svgBlob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
            const svgUrl = URL.createObjectURL(svgBlob);

            // Create an image element to draw the SVG onto the canvas
            const img = new Image();
            img.onload = async () => {
                if (!context) {
                    URL.revokeObjectURL(svgUrl);
                    return;
                }
                context.drawImage(img, 0, 0);
                URL.revokeObjectURL(svgUrl); // Clean up the object URL

                // Convert the canvas to a PNG data URL
                const pngUrl = canvas.toDataURL("image/png");

                // Trigger the download
                const link = document.createElement("a");
                link.href = pngUrl;
                link.download = "qr-code.png";
                link.click();

                // Copy the PNG to the clipboard
                const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/png"));
                if (blob) {
                    try {
                        await navigator.clipboard.write([
                            new ClipboardItem({ "image/png": blob }),
                        ]);
                        alert("QR code copied to clipboard!");
                    } catch (error) {
                        console.error("Failed to copy QR code to clipboard:", error);
                    }
                }
            };
            img.src = svgUrl;
        } catch (error) {
            console.error("Failed to download and copy QR code as PNG:", error);
        }
    };

    const handleCopyToClipboard = async () => {
        try {
            const copied = await copyUrlAsLink(content);
            if (copied) {
                toast.success(`URL copied to clipboard! ${content}`);
            } else {
                toast.error("Failed to copy URL to clipboard");
            }
        } catch (error) {
            console.error("Failed to copy URL to clipboard:", error);
            toast.error("Failed to copy URL to clipboard");
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            title="QR Code"
            onClose={onClose}
        >
                    <p>Click the QR code to download it as a PNG and copy it to the clipboard.</p>
                    <div className="qr-code" onClick={handleDownloadAndCopy} style={{ cursor: "pointer" }}>
                        <QRCodeSVG value={content} size={256} level="H" />
                    </div>
                    <p>Click the URL to copy it to the clipboard.</p>
                    <p onClick={handleCopyToClipboard} style={{ cursor: "pointer", color: "blue", textDecoration: "underline" }}>
                        {content}
                    </p>
        </Modal>
    );
}

